import { shortIdBuilder } from '@racemap/utilities/functions/utils';
import type { WMSLayerObject } from '@racemap/utilities/types/geos';
import { type FC, memo, useContext, useEffect, useRef } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import shortHash from 'shorthash2';
import { MapContext } from '../BasicMap/BasicMap';

export interface Props {
  properties: WMSLayerObject['properties'];
}

export const WMSRasterLayer: FC<Props> = memo(function WMSRasterLayer({ properties }: Props) {
  const { current: id } = useRef(`wms-raster-${shortIdBuilder()}`);
  const { mapStyle, map } = useContext(MapContext);

  useEffect(() => {
    if (map == null) return;

    // be sure, that external layers are under all other custom layers. "country-label" is the
    // last mapbox map layer of the vector tiles. If the map style is a satellite style, the layer "satellite" is
    // the last layer.
    const anchor = mapStyle?.sprite?.includes('satellite') ? 'satellite' : 'country-label';
    const moveUnderCustomLayers = () => {
      if (map.getLayer(`layer-${id}`) == null || map.getLayer(anchor) == null) return;
      map.moveLayer(`layer-${id}`, anchor);
    };

    // the layer can mount before the style is ready, for example when it comes from a late request
    if (map.isStyleLoaded()) moveUnderCustomLayers();
    else map.once('idle', moveUnderCustomLayers);

    return () => {
      map.off('idle', moveUnderCustomLayers);
    };
  }, [map, mapStyle?.sprite]);

  if (properties.tiles.length === 0 || !Number.isFinite(properties.tileSize)) return <></>;

  const hash = shortHash(properties.tiles.join('') + properties.tileSize.toString());

  return (
    <Source
      id={id}
      key={`${id}-${hash}`}
      type="raster"
      tiles={properties.tiles}
      tileSize={properties.tileSize}
    >
      <Layer id={`layer-${id}`} key={`layer-${id}`} type="raster" source={id} />
    </Source>
  );
});
